import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";

const CookiePolicy = () => {
  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section */}
      <section className="border-b border-border bg-gradient-to-br from-primary/5 to-accent/5">
        <div className="container mx-auto px-4 py-16 md:py-24">
          <div className="mx-auto max-w-3xl text-center">
            <h1 className="mb-6 text-4xl font-bold tracking-tight md:text-5xl">
              Cookie Policy
            </h1>
            <p className="text-lg text-muted-foreground">
              Last updated: November 11, 2025
            </p>
          </div>
        </div>
      </section>

      {/* Cookie Policy Content */}
      <section className="container mx-auto px-4 py-16 md:py-24">
        <div className="mx-auto max-w-4xl">
          <Card>
            <CardContent className="prose prose-slate max-w-none pt-6 dark:prose-invert">
              <div className="space-y-8">
                <div>
                  <h2 className="mb-4 text-2xl font-bold text-foreground">1. Introduction</h2>
                  <p className="text-muted-foreground">
                    This Cookie Policy explains how RunoBlog ("we", "our", or "us") uses cookies and similar tracking technologies when you visit www.runoblog.site. It describes what cookies are, how we use them, and how you can manage your cookie preferences.
                  </p>
                  <p className="mt-4 text-muted-foreground">
                    By continuing to browse our website, you agree to the use of cookies as described in this policy, unless you have disabled them.
                  </p>
                </div>

                <div>
                  <h2 className="mb-4 text-2xl font-bold text-foreground">2. What Are Cookies?</h2>
                  <p className="text-muted-foreground">
                    Cookies are small text files that are placed on your computer, tablet, or mobile device when you visit a website. They help websites recognize your device and store certain information about your preferences or past actions.
                  </p>
                </div>

                <div>
                  <h2 className="mb-4 text-2xl font-bold text-foreground">3. How We Use Cookies</h2>
                  <p className="text-muted-foreground">
                    We use cookies for several purposes, including:
                  </p>
                  <ul className="mt-4 space-y-2 text-muted-foreground">
                    <li><strong>Essential Cookies:</strong> Required for website functionality (e.g., security, navigation, and access to certain areas).</li>
                    <li><strong>Performance & Analytics Cookies:</strong> Help us understand how visitors interact with our site and improve usability.</li>
                    <li><strong>Advertising Cookies:</strong> Used by us and our advertising partners (like Google) to show personalized ads based on your interests.</li>
                    <li><strong>Functionality Cookies:</strong> Remember your preferences (such as language or region) to enhance your browsing experience.</li>
                  </ul>
                </div>

                <div>
                  <h2 className="mb-4 text-2xl font-bold text-foreground">4. Third-Party Cookies</h2>
                  <p className="text-muted-foreground">
                    Some cookies on our site are set by third-party services, such as:
                  </p>
                  <ul className="mt-4 space-y-2 text-muted-foreground">
                    <li><strong>Google Ads / AdSense / Analytics</strong> – for audience measurement and ad delivery.</li>
                    <li><strong>Adsterra</strong> – for popunder ads and audience targeting.</li>
                    <li><strong>Social Media Platforms</strong> – for sharing content and integrating social features.</li>
                  </ul>
                  <p className="mt-4 text-muted-foreground">
                    These third parties may use their own cookies to collect data about your online activity over time and across different websites. We do not control these cookies.
                  </p>
                </div>

                <div>
                  <h2 className="mb-4 text-2xl font-bold text-foreground">5. Managing or Disabling Cookies</h2>
                  <p className="text-muted-foreground">
                    You can manage or delete cookies at any time by adjusting your browser settings. Most browsers allow you to:
                  </p>
                  <ul className="mt-4 space-y-2 text-muted-foreground">
                    <li>Delete cookies that already exist on your device</li>
                    <li>Block cookies from being placed</li>
                    <li>Receive a warning before a cookie is stored</li>
                  </ul>
                  <p className="mt-4 text-muted-foreground">
                    Visit these pages for browser-specific help:
                  </p>
                  <ul className="mt-2 space-y-1 text-muted-foreground">
                    <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Google Chrome</a></li>
                    <li><a href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Mozilla Firefox</a></li>
                    <li><a href="https://support.apple.com/guide/safari/manage-cookies-and-website-data-sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Safari</a></li>
                    <li><a href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Microsoft Edge</a></li>
                  </ul>
                  <p className="mt-4 text-muted-foreground">
                    You can also withdraw or adjust your cookie preferences anytime via our Cookie Consent Tool (if available on our site).
                  </p>
                </div>

                <div>
                  <h2 className="mb-4 text-2xl font-bold text-foreground">6. Advertising Partners and Cookie Usage</h2>
                  <p className="text-muted-foreground">
                    We partner with Google, Adsterra, and other ad providers to display relevant ads. These partners may use cookies, web beacons, popunder ads, or other tracking technologies to collect data such as:
                  </p>
                  <ul className="mt-4 space-y-2 text-muted-foreground">
                    <li>IP address and browser information</li>
                    <li>Pages visited and time spent on site</li>
                    <li>Click behavior and ad engagement</li>
                  </ul>
                  <p className="mt-4 text-muted-foreground">
                    This information helps deliver ads that are more relevant to your interests.
                  </p>
                  <p className="mt-4 text-muted-foreground">
                    You can learn more about:
                  </p>
                  <ul className="mt-2 space-y-1 text-muted-foreground">
                    <li>Google's data usage: <a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">https://policies.google.com/technologies/partner-sites</a></li>
                    <li>Opt out of personalized ads (AdChoices): <a href="https://www.aboutads.info/choices/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">https://www.aboutads.info/choices/</a></li>
                  </ul>
                </div>

                <div>
                  <h2 className="mb-4 text-2xl font-bold text-foreground">7. Consent Under GDPR & CCPA</h2>
                  <p className="text-muted-foreground">
                    If you are located in the EU, EEA, or California, you have the right to control whether non-essential cookies are stored on your device. When you first visit our website, you will see a cookie banner asking for your consent to use cookies. You can accept or reject these at any time.
                  </p>
                </div>

                <div>
                  <h2 className="mb-4 text-2xl font-bold text-foreground">8. Updates to This Cookie Policy</h2>
                  <p className="text-muted-foreground">
                    We may update this Cookie Policy periodically. Any changes will be posted on this page with an updated "Last Updated" date.
                  </p>
                </div>

                <div>
                  <h2 className="mb-4 text-2xl font-bold text-foreground">9. Contact Us</h2>
                  <p className="text-muted-foreground">
                    If you have questions about this Cookie Policy, please contact us at:
                  </p>
                  <ul className="mt-4 space-y-2 text-muted-foreground">
                    <li>📧 Email: runoux.dev@gmail.com</li>
                    <li>🌐 Website: <a href="https://www.runoblog.site" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">https://www.runoblog.site</a></li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CookiePolicy;
